// Where there is still room, level by level.
//
// An "opening" is a weekday instruction group that is on the calendar and has
// fewer than four players. Three exclusions are deliberate:
//
//   - Weekend open play and privates aren't capacity-capped (spareSeats
//     returns null for them), so they would otherwise read as permanently
//     open.
//   - A group with no day and time has room on paper, but there is nothing to
//     tell a member to turn up for, so it isn't somewhere you can place them.
//   - "No Group" is a holding tier, not a level.
//
// The waitlist is reported but never subtracted: a queue is who asked, not a
// reservation, and the seat is still yours to fill.
import { DAYS, TIER_ORDER, isInstructionGroup } from "./tiers";
import { getGroupSchedule, parseTime } from "./time";
import { spareSeats, waitlistCount } from "./waitlist";

// Every level the openings list has a section for — TIER_ORDER without the
// "No Group" holding pen.
export const OPENING_TIERS = TIER_ORDER.filter(t => t !== "No Group");

export function isScheduled(group) {
  const { startTime, duration } = getGroupSchedule(group);
  return Boolean(group?.day && startTime && duration);
}

export function hasOpening(group) {
  if (!group || !isInstructionGroup(group)) return false;
  if (group.tier === "No Group") return false;
  if (!isScheduled(group)) return false;
  return (spareSeats(group) || 0) > 0;
}

// Week order, then time of day, then court — the order someone reads a
// schedule in, rather than the order groups happen to sit in the file.
export function compareBySlot(a, b) {
  const day = DAYS.indexOf(a.day) - DAYS.indexOf(b.day);
  if (day !== 0) return day;
  const start = (g) => parseTime(getGroupSchedule(g).startTime) ?? 0;
  const time = start(a) - start(b);
  if (time !== 0) return time;
  return String(a.court || "").localeCompare(String(b.court || ""));
}

// One entry per level, always all of them and always in TIER_ORDER: a level
// with nothing free is the most useful thing on the page, because it's where
// the next group needs opening.
//
// `tier` narrows to a single level (what picking a member does — their level,
// not their availability). Unknown tiers yield an empty list rather than
// silently showing everything.
export function openingsByTier(groups, { tier = null } = {}) {
  const open = (Array.isArray(groups) ? groups : []).filter(hasOpening);
  const tiers = tier ? OPENING_TIERS.filter(t => t === tier) : OPENING_TIERS;
  return tiers.map(t => {
    const matching = open.filter(g => g.tier === t).sort(compareBySlot);
    return {
      tier: t,
      groups: matching,
      seats: matching.reduce((n, g) => n + spareSeats(g), 0),
      waiting: matching.reduce((n, g) => n + waitlistCount(g), 0),
    };
  });
}

// Totals across whatever sections are being shown, for the summary line.
export function totalSeats(sections) {
  return (sections || []).reduce((n, s) => n + s.seats, 0);
}

export function totalGroups(sections) {
  return (sections || []).reduce((n, s) => n + s.groups.length, 0);
}
